import { useEffect, useState } from 'react';
import { useGameStore } from '../../core/store';
import { useTunnelExitStore } from './tunnelExitStore';
import { SkillsCheckpointBar } from './SkillsCheckpointBar';
import { NextSkillCard } from './NextSkillCard';
import { TunnelRadar } from './TunnelRadar';
import { TunnelObjectiveTip } from './TunnelObjectiveTip';
import './tunnel-hud.css';

const STAMINA_SEGS = 14;
/** Let iris open / tunnel read first, then show controls */
const GUIDE_DELAY_MS = 4800;

function TunnelControlsGuide({
  onDismiss,
}: {
  onDismiss: () => void;
}) {
  return (
    <div className="tunnel-hud__guide" role="dialog" aria-label="Tunnel controls">
      <div className="tunnel-hud__guide-top">
        <p className="tunnel-hud__guide-kicker">How to walk the tunnel</p>
        <button
          type="button"
          className="tunnel-hud__guide-skip"
          onClick={onDismiss}
        >
          Skip
        </button>
      </div>
      <div className="tunnel-hud__guide-grid">
        <div className="tunnel-hud__guide-card">
          <div className="tunnel-hud__keys" aria-hidden>
            <span className="tunnel-hud__key tunnel-hud__key--ghost" />
            <span className="tunnel-hud__key">W</span>
            <span className="tunnel-hud__key tunnel-hud__key--ghost" />
            <span className="tunnel-hud__key">A</span>
            <span className="tunnel-hud__key">S</span>
            <span className="tunnel-hud__key">D</span>
          </div>
          <strong>Move</strong>
          <p>
            <b>W</b> forward · <b>S</b> back
            <br />
            <b>A</b> left · <b>D</b> right
          </p>
        </div>

        <div className="tunnel-hud__guide-card">
          <div className="tunnel-hud__look-icons" aria-hidden>
            <svg viewBox="0 0 64 40" className="tunnel-hud__icon-mouse">
              <rect
                x="22"
                y="4"
                width="20"
                height="32"
                rx="10"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
              <line
                x1="32"
                y1="8"
                x2="32"
                y2="16"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M12 20h8M44 20h8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span className="tunnel-hud__or">or</span>
            <svg viewBox="0 0 72 40" className="tunnel-hud__icon-pad">
              <rect
                x="8"
                y="8"
                width="56"
                height="24"
                rx="4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M28 16v12M36 14v16M44 16v12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.85"
              />
            </svg>
          </div>
          <strong>Look / turn</strong>
          <p>
            <b>Mouse</b> — click, then move
            <br />
            <b>Trackpad</b> — two fingers: left → look left
          </p>
        </div>
      </div>
      <button type="button" className="tunnel-hud__guide-go" onClick={onDismiss}>
        Got it — walk toward the light
      </button>
    </div>
  );
}

export function TunnelHUD() {
  const walking = useGameStore((s) => s.tunnelWalking);
  const stamina = useGameStore((s) => s.tunnelStamina);
  const toast = useGameStore((s) => s.careerToast);
  const toastAt = useGameStore((s) => s.careerToastAt);
  const clearToast = useGameStore((s) => s.clearCareerToast);
  const exitBeat = useTunnelExitStore((s) => s.beat);
  const [showGuide, setShowGuide] = useState(false);
  const [objectiveReady, setObjectiveReady] = useState(false);

  const staminaLit = Math.round(Math.max(0, Math.min(1, stamina)) * STAMINA_SEGS);

  const dismissGuide = () => {
    setShowGuide(false);
    setObjectiveReady(true);
  };

  useEffect(() => {
    if (!walking) {
      setShowGuide(false);
      setObjectiveReady(false);
      return;
    }
    setShowGuide(false);
    setObjectiveReady(false);
    const show = window.setTimeout(() => setShowGuide(true), GUIDE_DELAY_MS);
    return () => window.clearTimeout(show);
  }, [walking]);

  useEffect(() => {
    if (!showGuide || !walking) return;
    const onKey = (e: KeyboardEvent) => {
      if (
        e.code === 'Enter' ||
        e.code === 'Space' ||
        e.code === 'Escape'
      ) {
        dismissGuide();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showGuide, walking]);

  useEffect(() => {
    if (!toast || !toastAt) return;
    const id = window.setTimeout(() => {
      clearToast();
    }, 2300);
    return () => window.clearTimeout(id);
  }, [toast, toastAt, clearToast]);

  if (!walking) return null;
  if (exitBeat !== 'fp') return null;

  return (
    <div className="tunnel-hud">
      <TunnelObjectiveTip ready={objectiveReady} />
      <NextSkillCard />
      <SkillsCheckpointBar />
      <TunnelRadar />

      <div className="tunnel-hud__stamina">
        <span>Stamina</span>
        <div
          className="tunnel-hud__stamina-segs"
          aria-label={`Stamina ${Math.round(stamina * 100)}%`}
        >
          {Array.from({ length: STAMINA_SEGS }, (_, i) => {
            const on = i < staminaLit;
            const peak = on && i >= staminaLit - 2 && staminaLit > 2;
            return (
              <i
                key={i}
                className={`${on ? 'is-on' : ''}${peak ? ' is-peak' : ''}`.trim()}
              />
            );
          })}
        </div>
      </div>

      {showGuide && <TunnelControlsGuide onDismiss={dismissGuide} />}
    </div>
  );
}
